/**
 * View model of the analyst tattoo-matching screen (PRD-0032 UC-7, SC-027..SC-029). These
 * types belong to the tattoo-match group alone; the shared domain types live in ../types.
 */
import type { SubjectPageProblem } from '../subject/SubjectPageNotice'
import type {
  BodyRegionId,
  EncounterType,
  InkColour,
  MarkKind,
  MarkSummary,
  SubjectSummary,
  TattooPair,
  Type10Class,
} from '../types'

/** The owner of a mark, as the tattoo screens show it. */
export type MarkOwner = Pick<
  SubjectSummary,
  'id' | 'subjectNo' | 'captureTag' | 'givenName' | 'surname' | 'sex' | 'portraitUrl'
>

/** The encounter at which the mark was photographed. */
export interface MarkEncounterRef {
  type: EncounterType
  at: string
  placeNote?: string | null
}

/** A mark with its owner resolved: one queue card, one side of the pair view. */
export interface TattooMarkView {
  mark: MarkSummary
  owner: MarkOwner
  encounter?: MarkEncounterRef | null
}

/** One suggested pair seen from the query mark: the evidence plus the other mark. */
export interface TattooCandidateView {
  pair: TattooPair
  candidate: TattooMarkView
}

/** Candidates the matcher found but kept under the suggestion threshold (not shown). */
export interface HiddenCandidates {
  count: number
  /** Inliers of the best hidden one. */
  bestInliers: number
}

export type MarkQueueStatus = 'pending' | 'decided'

export interface MarkQueueFilters {
  regionIds: readonly BodyRegionId[]
  kinds: readonly MarkKind[]
  classes: readonly Type10Class[]
  colours: readonly InkColour[]
  /** Free text over the mark descriptions (hybrid search on the server). */
  text: string
  status: MarkQueueStatus
}

export const EMPTY_MARK_FILTERS: MarkQueueFilters = {
  regionIds: [],
  kinds: [],
  classes: [],
  colours: [],
  text: '',
  status: 'pending',
}

/**
 * Why a read of the screen came back without an answer, read as the subject page's: the mark is
 * gone, the read could not be recorded in the audit trail (so nothing was returned), no
 * connection, or anything else — a description search that could not run among them, which
 * trying again can help.
 */
export type TattooReadProblem = SubjectPageProblem

/** The queue column: the (already filtered) marks, the selection and the region counts. */
export interface MarkQueueData {
  items: readonly TattooMarkView[]
  selectedMarkId: string | null
  /** Marks per region before the region filter: which regions of the mini map are clickable. */
  regionCounts: Partial<Record<BodyRegionId, number>>
  /** The last read for these filters failed: the list says why instead of showing marks. */
  problem?: TattooReadProblem | null
  /** That read is being made again: the notice stays, its *Try again* waiting. */
  retrying?: boolean
  /** The text matched exact words only (the meaning-based search did not run): a hint says so. */
  degraded?: boolean
}

/** The pair view of one query mark: its candidates and the selected one. */
export interface TattooReview {
  query: TattooMarkView
  candidates: readonly TattooCandidateView[]
  selectedPairId: string | null
  hidden?: HiddenCandidates | null
}

/** The centre column: no mark picked yet, the picked mark's candidates loading or failed, or its review. */
export type TattooReviewState =
  | { status: 'none' }
  | { status: 'loading' }
  | { status: 'failed'; problem: TattooReadProblem }
  | { status: 'ready'; review: TattooReview }

export type TattooDecision = 'same' | 'different'

/** Decisions on a pair. Omit them for a viewer: the pair view is then read-only (SC-036). */
export interface PairDecisionActions {
  /** A decision is on its way: no other is taken until it is answered. */
  busy?: boolean
  onDecide: (pairId: string, decision: TattooDecision, note: string) => void
  /** Reopen a decided pair (audited like the decision itself). */
  onReopen: (pairId: string) => void
  /** "Merge subjects": entity resolution's audited, reversible merge (reason tattoo_match). */
  onMerge: (pairId: string) => void
  /** "Different persons, shared motif": an affiliation lead, no merge. */
  onMotifOnly: (pairId: string) => void
}

/**
 * Where a pair stands, as the analyst reads it. A confirmed pair on two subjects who were never
 * seen together raises the merge proposal (a tattoo is a design, not a person).
 */
export type PairOutcome =
  | 'pending'
  | 'different'
  | 'same'
  | 'sameSeenTogether'
  | 'motif'
  | 'mergeProposal'

export function pairOutcome(
  pair: Pick<TattooPair, 'status' | 'motifOnly' | 'seenTogether'>,
  crossSubject: boolean
): PairOutcome {
  if (pair.status === 'suggested') return 'pending'
  if (pair.status === 'rejected') return 'different'
  if (!crossSubject) return 'same'
  if (pair.seenTogether) return 'sameSeenTogether'
  return pair.motifOnly ? 'motif' : 'mergeProposal'
}
